// धातुरूप प्रयोगशाला (Dhātu laboratory): root → tense → person → number on the left, the result on the right: the
// root card, then Table · Formation · Flashcards · Practice · Compare tenses · Sentences. The table's chosen cell is
// used in a sentence on the dark card under it. ?root=पढ&tense=bhavishya opens a table; ?view=compare opens a tab.

import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { GridCell } from '@/prakrit/components/grammar/grammar-grid'
import type { Num, Person, Tense } from '@/prakrit/lib/grammar/types'
import type { LabRoot } from '@/prakrit/components/grammar/labs/verb-data'

import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
    ArrowRight,
    BookOpen,
    Columns3,
    Info,
    Layers,
    ListChecks,
    MessageSquareText,
    Table2,
    Workflow
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { FREE_TENSE } from '@/lib/plans'
import { useSession } from '@/lib/client/session'
import { verbCells } from '@/prakrit/lib/grammar/cells'
import { verbFormation } from '@/prakrit/lib/grammar/formation'
import { NUMBERS, PERSONS } from '@/prakrit/lib/grammar/types'
import {
    NUMBER_LABEL,
    PERSON_LABEL,
    PERSON_SHORT,
    TENSE_LABEL
} from '@/prakrit/lib/grammar/labels'
import { useLemmaStatuses } from '@/hooks/use-statuses'
import { useReducedMotion } from '@/hooks/use-media-query'
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger
} from '@/components/ui/tabs'
import { Bi } from '@/prakrit/components/common/bi'
import { PlanNotice } from '@/components/plans/plan-notice'
import { StatusBadge } from '@/prakrit/components/common/status-badge'
import { FormationPlayer } from '@/prakrit/components/grammar/labs/formation-player'
import { TenseCompare } from '@/prakrit/components/grammar/labs/tense-compare'
import { VerbFlashcards } from '@/prakrit/components/grammar/labs/verb-flashcards'
import { useUrlQuery } from '@/prakrit/components/grammar/labs/use-url-query'
import {
    CONTAINER,
    ControlLabel,
    LabNav,
    Notice,
    useKidsMode
} from '@/prakrit/components/grammar/labs/lab-ui'
import {
    CELL_ORDER,
    advancedTensesOf,
    basicTensesOf,
    findRoot,
    isLabTense,
    labRoots,
    tenseNote
} from '@/prakrit/components/grammar/labs/verb-data'
import { RootTiles } from '@/prakrit/components/grammar/dhatu/root-tiles'
import { kalHref } from '@/prakrit/components/grammar/kal/kal-data'
import { TenseChips } from '@/prakrit/components/grammar/dhatu/tense-chips'
import { RootHeader } from '@/prakrit/components/grammar/dhatu/root-header'
import { VerbTable } from '@/prakrit/components/grammar/dhatu/verb-table'
import { SentenceCard } from '@/prakrit/components/grammar/dhatu/sentence-card'
import { SentencesView } from '@/prakrit/components/grammar/dhatu/sentences-view'
import { subjectOf } from '@/prakrit/components/grammar/dhatu/meaning'
import { NUMBER_EN, PERSON_EN } from '@/prakrit/components/grammar/dhatu/labels'
import {
    PANEL,
    PageIntro,
    PillGroup,
    StepCard
} from '@/prakrit/components/grammar/dhatu/dhatu-ui'

const DEFAULT_ROOT = 'पढ'

type View = 'table' | 'formation' | 'cards' | 'practice' | 'compare' | 'sentences'

const VIEWS: { value: View, hi: string, en: string, icon: LucideIcon }[] = [
    { value: 'table', hi: 'तालिका', en: 'Table', icon: Table2 },
    { value: 'formation', hi: 'रूप-निर्माण', en: 'Formation', icon: Workflow },
    { value: 'cards', hi: 'स्मृति-कार्ड', en: 'Flashcards', icon: Layers },
    { value: 'practice', hi: 'अभ्यास', en: 'Practice', icon: ListChecks },
    { value: 'compare', hi: 'काल-तुलना', en: 'Compare tenses', icon: Columns3 },
    { value: 'sentences', hi: 'वाक्य', en: 'Sentences', icon: MessageSquareText }
]

const isView = (value: string): value is View => VIEWS.some(v => v.value === value)

const PRACTICE = [
    {
        href: '/practice/roop-sadhana',
        hi: 'रूप साधना',
        en: 'Complete the table',
        desc: {
            hi: 'तालिका के खाली खानों में सही रूप भरिए।',
            en: 'Fill the empty cells of the table with the right forms.'
        }
    },
    {
        href: '/practice/dhatu-chunauti',
        hi: 'धातु चुनौती',
        en: 'Root challenge',
        desc: {
            hi: 'इस धातु के रूपों पर छोटे प्रश्न: सही रूप चुनिए।',
            en: 'Short questions on the forms of this root: pick the right one.'
        }
    }
]

const TRIGGER = cn(
    'h-auto min-h-tap flex-none gap-2 rounded-full border border-border bg-card px-4 py-1.5 text-sm font-medium text-foreground shadow-none',
    'hover:border-saffron/60 dark:text-foreground',
    'data-active:border-foreground data-active:bg-foreground data-active:text-background data-active:shadow-sm',
    'dark:data-active:border-foreground dark:data-active:bg-foreground dark:data-active:text-background'
)

const sameInEveryCell = (cells: Record<Person, Record<Num, GridCell>>): boolean => {
    const first = cells.uttam.eka.core.join('|')

    return CELL_ORDER.every(([p, n]) => cells[p][n].core.join('|') === first)
}

const tableReview = (cells: Record<Person, Record<Num, GridCell>>): string => {
    const reviews = CELL_ORDER.map(([p, n]) => cells[p][n].review ?? 'needs_review')

    if (reviews.every(r => r === 'published')) return 'published'
    if (reviews.every(r => r === 'verified' || r === 'published')) return 'verified'

    return 'needs_review'
}

interface FormationViewProps {
    root: LabRoot
    tense: Tense
    cells: Record<Person, Record<Num, GridCell>>
    person: Person
    number: Num
    showAlt: boolean
    onSelect: (person: Person, number: Num) => void
}

/** The blackboard for one cell, with the six cells beside it so another form can be built at once. */
const FormationView: FC<FormationViewProps> = ({ root, tense, cells, person, number, showAlt, onSelect }): ReactNode => {
    const cell = cells[person][number]
    const forms = [...cell.core, ...(showAlt ? cell.alt : [])]
    const [chosen, setChosen] = useState<string | null>(null)
    const form = chosen && forms.includes(chosen) ? chosen : forms[0] ?? ''
    const entry = root.tables[tense]?.[person]?.[number]?.find(f => f.form === form)
    const note = tenseNote(tense, root.group)
    const formation = useMemo(
        () => (form && root.group !== 'as' ? verbFormation(root.root, root.rootClass, tense, person, number, form, entry?.rule) : null),
        [form, root, tense, person, number, entry]
    )

    return (
        <div className='grid gap-6 xl:grid-cols-[15rem_minmax(0,1fr)] xl:items-start'>
            <div className='flex flex-col gap-2'>
                <ControlLabel id='formation-cells' hi='कौन-सा रूप बनाएँ?' en='Which form to build?' />
                <ul aria-labelledby='formation-cells' className='grid grid-cols-2 gap-2 xl:grid-cols-1'>
                    {CELL_ORDER.map(([p, n]) => {
                        const active = p === person && n === number

                        return (
                            <li key={`${p}.${n}`}>
                                <button
                                    type='button'
                                    aria-pressed={active}
                                    onClick={() => {
                                        setChosen(null)
                                        onSelect(p, n)
                                    }}
                                    className={cn(
                                        'flex min-h-tap w-full flex-col items-start justify-center rounded-2xl border px-3.5 py-2 text-left transition-colors',
                                        active ? 'border-saffron bg-saffron/15' : 'border-border bg-card hover:border-saffron/60'
                                    )}
                                >
                                    <span className='flex flex-wrap items-baseline gap-x-2'>
                                        <span lang='pra' className='font-prakrit text-sm font-medium text-muted-foreground'>{subjectOf(p, n)}</span>
                                        <span lang='pra' className='font-prakrit text-lg font-bold text-prakrit'>{cells[p][n].core[0] ?? '—'}</span>
                                    </span>
                                    <span lang='hi' className='hindi text-xs text-muted-foreground'>{PERSON_LABEL[p].hi} {NUMBER_LABEL[n].hi}</span>
                                </button>
                            </li>
                        )
                    })}
                </ul>
            </div>

            <div className='flex min-w-0 flex-col gap-4'>
                {forms.length > 1 && (
                    <div className='flex flex-col gap-2'>
                        <ControlLabel id='formation-forms' hi='इस खाने के रूप' en='Forms of this cell' />
                        <div role='group' aria-labelledby='formation-forms' className='flex flex-wrap gap-2'>
                            {forms.map(f => {
                                const alt = cell.alt.includes(f)

                                return (
                                    <button
                                        key={f}
                                        type='button'
                                        aria-pressed={f === form}
                                        onClick={() => setChosen(f)}
                                        className={cn(
                                            'inline-flex min-h-tap items-center gap-2 rounded-full border px-4 py-1 transition-colors',
                                            f === form ? 'border-foreground bg-foreground text-background' : 'border-border bg-card hover:border-saffron/60'
                                        )}
                                    >
                                        <span lang='pra' className={cn('font-prakrit text-lg font-bold', f === form ? 'text-background' : alt ? 'text-terracotta' : 'text-prakrit')}>{f}</span>
                                        {alt && <StatusBadge kind='alt' withEnglish={false} />}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}

                {formation ? (
                    <FormationPlayer
                        formation={formation}
                        caption={(
                            <span className='flex flex-wrap gap-x-2'>
                                <span lang='hi' className='hindi'>{TENSE_LABEL[tense].hi} · {PERSON_LABEL[person].hi} {NUMBER_LABEL[number].hi}</span>
                                <span lang='en' data-ex='en'>({TENSE_LABEL[tense].en}, {NUMBER_LABEL[number].en})</span>
                            </span>
                        )}
                    />
                ) : (
                    <Notice icon={<Info className='size-4 text-saffron-strong' />}>
                        <Bi
                            hi='अस (होना) के रूप किसी नियम से नहीं बनते — ये अनियमित रूप हैं, इन्हें वैसे ही याद करें।'
                            en='The forms of अस (to be) are not built by a rule: they are irregular, so learn them as they are.'
                            className='gap-0.5'
                        />
                        <p lang='pra' className='pra pra-lg'>{forms.join(' / ')}</p>
                    </Notice>
                )}

                <section aria-label='नियम · The rule' className='flex flex-col gap-1.5 rounded-2xl bg-surface-muted/70 px-4 py-3'>
                    <h3 className='flex flex-wrap items-center gap-2 font-sans text-sm font-semibold'>
                        <BookOpen className='size-4 text-saffron-strong' aria-hidden='true' />
                        <Bi hi='इस काल का नियम' en='The rule of this tense' layout='inline' />
                        <span lang='hi' className='hindi rounded-full border border-border bg-card px-2 py-0.5 text-xs font-medium'>{entry?.rule ?? note.rule}</span>
                    </h3>
                    <Bi hi={note.hi} en={note.en} className='gap-1 text-sm leading-relaxed' />
                </section>
            </div>
        </div>
    )
}

export const KriyaLab: FC = (): ReactNode => {
    const roots = labRoots()
    const kids = useKidsMode()
    const reduced = useReducedMotion()
    const { access, loading: sessionLoading } = useSession()
    const tenseLocked = (t: Tense): boolean => !sessionLoading && !access.full && t !== FREE_TENSE
    const url = useUrlQuery()
    const [rootId, setRootId] = useState(DEFAULT_ROOT)
    const [tense, setTense] = useState<Tense>('vartaman')
    const [view, setView] = useState<View>('table')
    const [cell, setCell] = useState<{ person: Person, number: Num }>({ person: 'uttam', number: 'eka' })
    const tabsRef = useRef<HTMLDivElement>(null)
    const viewFromUrl = useRef(false)

    // The URL chooses the root and tense (?root=पढ&tense=bhavishya), also when a link on this page changes it.
    const urlRoot = url.read('root')
    const urlTense = url.read('tense')
    const urlView = url.read('view')

    useEffect(() => {
        if (!url.ready) return

        setRootId(urlRoot && findRoot(urlRoot) ? urlRoot : DEFAULT_ROOT)
        setTense(urlTense && isLabTense(urlTense) ? urlTense : 'vartaman')
    }, [url.ready, urlRoot, urlTense])

    // ?view=compare (the Grammar Lab's "tenses" card) opens a tab once, when the page loads.
    useEffect(() => {
        if (!url.ready || viewFromUrl.current) return

        viewFromUrl.current = true

        if (urlView && isView(urlView)) setView(urlView)
    }, [url.ready, urlView])

    const root = findRoot(rootId) ?? roots[0]
    const basic = basicTensesOf(root)
    const advanced = advancedTensesOf(root)
    const available = kids ? basic : [...basic, ...advanced]
    const activeTense = available.includes(tense) ? tense : basic[0]
    const altOn = !kids
    const status = useLemmaStatuses('verb_form', root.root)
    const table = root.tables[activeTense]
    const cells = useMemo(() => verbCells(root.root, activeTense, table, status), [root.root, activeTense, table, status])
    const selected = cells[cell.person][cell.number]
    const allSame = sameInEveryCell(cells)

    const selectRoot = (next: string) => {
        const r = findRoot(next)

        if (!r) return

        const tenses = kids ? basicTensesOf(r) : [...basicTensesOf(r), ...advancedTensesOf(r)]
        const t = tenses.includes(activeTense) ? activeTense : 'vartaman'

        setRootId(r.root)
        setTense(t)
        url.write({ root: r.root, tense: t })
    }

    const selectTense = (t: Tense) => {
        setTense(t)
        url.write({ root: root.root, tense: t })
    }

    const selectCell = (person: Person, number: Num) => setCell({ person, number })

    const changeView = (next: View) => {
        setView(next)
        url.write({ view: next === 'table' ? null : next })
    }

    const showFormation = () => {
        changeView('formation')

        const top = tabsRef.current

        if (top && top.getBoundingClientRect().top < 0) top.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' })
    }

    return (
        <>
            <PageIntro
                eyebrow='धातुरूपप्रयोगशाला'
                title={{ en: 'Dhātu laboratory', hi: 'धातुरूप प्रयोगशाला' }}
                intro={{
                    hi: 'धातु → काल → पुरुष → वचन चुनिए। हर रूप नियम से बनता है, केवल तालिका से देखा नहीं जाता।',
                    en: 'Choose root → tense → person → number. Every form is built by rule.'
                }}
                kidsIntro={{
                    hi: 'एक धातु चुनो, फिर कोई खाना छुओ — देखो रूप कैसे बनता है!',
                    en: 'Pick a root, then tap a box to see how the word is made!'
                }}
            />

            <div className={CONTAINER}>
                <div className='grid gap-6 lg:grid-cols-[20rem_minmax(0,1fr)] lg:items-start xl:grid-cols-[21.5rem_minmax(0,1fr)]'>
                    <div className='grid gap-4 md:grid-cols-2 md:items-start lg:grid-cols-1'>
                        <StepCard id='step-root' step={1} hi='धातु' en='Root' className='md:col-span-2 lg:col-span-1'>
                            <RootTiles roots={roots} value={root.root} onChange={selectRoot} moreLabelId='step-root-more' />
                        </StepCard>

                        <StepCard id='step-tense' step={2} hi='काल' en='Tense'>
                            <TenseChips basic={basic} advanced={advanced} value={activeTense} onChange={selectTense} kids={kids} locked={tenseLocked} />
                        </StepCard>

                        <StepCard id='step-person' step={3} hi='पुरुष · वचन' en='Person · number'>
                            <div className='flex flex-wrap gap-2'>
                                <PillGroup
                                    label='पुरुष · Person'
                                    value={cell.person}
                                    options={PERSONS.map(p => ({
                                        value: p,
                                        title: `${PERSON_LABEL[p].hi} · ${PERSON_EN[p]}`,
                                        label: <span lang='hi' className='hindi'>{PERSON_SHORT[p].hi}</span>
                                    }))}
                                    onChange={p => selectCell(p, cell.number)}
                                />
                                <PillGroup
                                    label='वचन · Number'
                                    value={cell.number}
                                    options={NUMBERS.map(n => ({
                                        value: n,
                                        title: `${NUMBER_LABEL[n].hi} · ${NUMBER_LABEL[n].en}`,
                                        label: <span lang='hi' className='hindi'>{n === 'eka' ? 'एक' : 'बहु'}</span>
                                    }))}
                                    onChange={n => selectCell(cell.person, n)}
                                />
                            </div>
                            <p className='text-xs text-muted-foreground'>
                                <span lang='hi' className='hindi font-medium text-foreground/80'>{PERSON_LABEL[cell.person].hi} {NUMBER_LABEL[cell.number].hi}</span>
                                <span aria-hidden='true'> · </span>
                                <span lang='en'>{PERSON_EN[cell.person]}, {NUMBER_LABEL[cell.number].en}</span>
                            </p>
                        </StepCard>
                    </div>

                    <div className='flex min-w-0 flex-col gap-5'>
                        <RootHeader root={root} tense={activeTense} review={tableReview(cells)} />

                        {tenseLocked(activeTense) ? <PlanNotice kind='grammar' compact /> : (
                        <Tabs value={view} onValueChange={value => changeView(value as View)} className='gap-5'>
                            <div ref={tabsRef} className='scroll-mt-24'>
                                <TabsList
                                    aria-label='दृश्य · View'
                                    className='no-scrollbar h-auto w-full justify-start gap-2 overflow-x-auto rounded-none bg-transparent p-0.5 group-data-horizontal/tabs:h-auto'
                                >
                                    {VIEWS.map(v => {
                                        const Icon = v.icon

                                        return (
                                            <TabsTrigger key={v.value} value={v.value} className={TRIGGER}>
                                                <Icon className='size-4' aria-hidden='true' />
                                                <span lang='hi' className='hindi'>{v.hi}</span>
                                                <span lang='en' className='text-xs font-normal opacity-75'>{v.en}</span>
                                            </TabsTrigger>
                                        )
                                    })}
                                </TabsList>
                            </div>

                            <TabsContent value='table' className='flex flex-col gap-4'>
                                <VerbTable
                                    cells={cells}
                                    selected={cell}
                                    showAlt={altOn}
                                    onSelect={selectCell}
                                    label={`${root.root} · ${TENSE_LABEL[activeTense].hi} · पुरुष × वचन`}
                                />
                                <div className='flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-muted-foreground'>
                                    <Bi
                                        hi='किसी भी खाने पर टैप करें, या तीर-कुंजियों से घूमें।'
                                        en='Tap any cell, or move with the arrow keys.'
                                        layout='inline'
                                    />
                                    {allSame && (
                                        <span className='rounded-full bg-secondary px-2.5 py-1 text-foreground'>
                                            <span lang='hi' className='hindi'>सभी खानों में एक ही रूप</span>
                                            <span aria-hidden='true' className='ex-sep'> · </span>
                                            <span lang='en' data-ex='en' className='text-muted-foreground'>one form for every cell</span>
                                        </span>
                                    )}
                                    {altOn && CELL_ORDER.some(([p, n]) => cells[p][n].alt.length > 0) && (
                                        <span className='adults-only inline-flex items-center gap-1.5'>
                                            <span aria-hidden='true' className='size-2 rounded-full bg-terracotta' />
                                            <span lang='hi' className='hindi'>लाल रंग में विकल्प रूप</span>
                                            <span aria-hidden='true' className='ex-sep'> · </span>
                                            <span lang='en' data-ex='en'>variant forms in terracotta</span>
                                        </span>
                                    )}
                                </div>
                                <SentenceCard
                                    root={root}
                                    tense={activeTense}
                                    person={cell.person}
                                    number={cell.number}
                                    cell={selected}
                                    showAlt={altOn}
                                    onHowBuilt={showFormation}
                                />
                                {root.group !== 'as' && (
                                    <Link
                                        href={kalHref(activeTense, root.root)}
                                        className='group inline-flex min-h-tap items-center gap-2 self-start rounded-full px-1 text-sm font-semibold text-saffron-strong hover:underline'
                                    >
                                        <span lang='hi' className='hindi'>{TENSE_LABEL[activeTense].hi}: सामान्य और विकल्प प्रत्यय</span>
                                        <span aria-hidden='true'>·</span>
                                        <span lang='en'>Standard and variant endings</span>
                                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                                    </Link>
                                )}
                            </TabsContent>

                            <TabsContent value='formation'>
                                <FormationView
                                    key={`${root.root}|${activeTense}|${cell.person}|${cell.number}`}
                                    root={root}
                                    tense={activeTense}
                                    cells={cells}
                                    person={cell.person}
                                    number={cell.number}
                                    showAlt={altOn}
                                    onSelect={selectCell}
                                />
                            </TabsContent>

                            <TabsContent value='cards' className='flex flex-col gap-4'>
                                <p className='flex flex-wrap items-baseline gap-x-2 text-sm'>
                                    <span lang='pra' className='pra pra-sm'>{root.root}</span>
                                    <span lang='hi' className='hindi font-semibold'>{TENSE_LABEL[activeTense].hi}</span>
                                    <span lang='en' className='text-muted-foreground'>{TENSE_LABEL[activeTense].en}: six cards, one for each cell</span>
                                </p>
                                <VerbFlashcards key={`${root.root}|${activeTense}`} root={root} tense={activeTense} showAlt={altOn} />
                            </TabsContent>

                            <TabsContent value='practice' className='flex flex-col gap-4'>
                                <ul className='grid gap-4 md:grid-cols-2'>
                                    {PRACTICE.map(p => (
                                        <li key={p.href}>
                                            <Link
                                                href={`${p.href}?root=${encodeURIComponent(root.root)}`}
                                                className={cn(PANEL, 'group flex h-full flex-col gap-3 p-5 transition-colors hover:border-saffron focus-visible:border-saffron')}
                                            >
                                                <span className='flex items-center justify-between gap-3'>
                                                    <span className='flex flex-col'>
                                                        <span lang='hi' className='literary text-lg text-saffron-strong'>{p.hi}</span>
                                                        <span lang='en' className='text-lg font-semibold'>{p.en}</span>
                                                    </span>
                                                    <ArrowRight className='size-5 shrink-0 text-saffron-strong transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                                                </span>
                                                <Bi hi={p.desc.hi} en={p.desc.en} className='gap-0.5 text-[0.9375rem]' />
                                                <span className='mt-auto flex flex-wrap items-baseline gap-x-2 text-sm'>
                                                    <span lang='hi' className='hindi text-muted-foreground'>धातु</span>
                                                    <span lang='pra' className='pra pra-sm'>{root.root}</span>
                                                    <span lang='hi' className='hindi text-muted-foreground'>({root.hi})</span>
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                                <Notice icon={<Info className='size-4 text-saffron-strong' />}>
                                    <Bi
                                        hi='हर रूप पुस्तक के नियमों से बनता है, और उत्तर सर्वर पर जाँचे जाते हैं।'
                                        en='Every form is built from the rules of the book, and answers are checked on the server.'
                                        className='gap-0.5'
                                    />
                                </Notice>
                            </TabsContent>

                            <TabsContent value='compare'>
                                {!access.full && !sessionLoading ? <PlanNotice kind='grammar' compact /> : (
                                <TenseCompare
                                    root={root}
                                    person={cell.person}
                                    number={cell.number}
                                    tense={activeTense}
                                    showAlt={altOn}
                                    advanced={!kids}
                                    onPickTense={t => {
                                        selectTense(t)
                                        changeView('table')
                                    }}
                                    onSelectCell={selectCell}
                                />
                                )}
                            </TabsContent>

                            <TabsContent value='sentences'>
                                <SentencesView
                                    root={root}
                                    tense={activeTense}
                                    person={cell.person}
                                    number={cell.number}
                                    onSelect={selectCell}
                                />
                            </TabsContent>
                        </Tabs>
                        )}
                    </div>
                </div>

                <LabNav current='/grammar/kriya' />
                <p className='sr-only' aria-live='polite'>
                    {PERSON_LABEL[cell.person].hi} {NUMBER_LABEL[cell.number].hi} ({PERSON_EN[cell.person]}, {NUMBER_EN[cell.number]}): {selected.core.join(', ')}
                </p>
                <div className='h-16' aria-hidden='true' />
            </div>
        </>
    )
}
